"use client";

import type { ReactNode } from "react";
import type { Service } from "vanilla-cookieconsent";

import { useEffect } from "react";
import { acceptService, getUserPreferences, run } from "vanilla-cookieconsent";
import "@orestbida/iframemanager/dist/iframemanager.css";
import "@orestbida/iframemanager/dist/iframemanager.js";
import "vanilla-cookieconsent/dist/cookieconsent.css";

import type { CookieService } from "@/src/infrastructure/cookie-consent/CookieService";
import type { IframeManagerChange } from "@/src/infrastructure/cookie-consent/IframeManager";

import { airtableCookie } from "@/src/infrastructure/airtable/airtable";
import { CookieCategory } from "@/src/infrastructure/cookie-consent/CookieCategory";
import {
  iframeLanguages,
  language,
  showPreferencesLabel,
} from "@/src/infrastructure/cookie-consent/cookieConsentI18n";
import { datadogCookie } from "@/src/infrastructure/datadog/Datadog";
import { youtubeCookie } from "@/src/infrastructure/youtube/YoutubeVideo";

export function CookieConsent(): ReactNode {
  const iframeManager =
    typeof iframemanager === "undefined" ? null : iframemanager();

  // https://cookieconsent.orestbida.com/advanced/iframemanager-setup.html
  function iframeManagerOnChange(change: IframeManagerChange): void {
    if (change.eventSource.type !== "click") {
      return;
    }
    const currentFeatureServices =
      getUserPreferences().acceptedServices[CookieCategory.FEATURES] ?? [];
    acceptService(
      [...currentFeatureServices, ...change.changedServices],
      CookieCategory.FEATURES,
    );
  }

  useEffect(() => {
    if (!iframeManager) {
      return;
    }

    function iframeService(service: CookieService): Service {
      return {
        onAccept: (): void => {
          iframeManager?.acceptService(service.name);
        },
        onReject: (): void => {
          iframeManager?.rejectService(service.name);
        },
      };
    }

    iframeManager.run({
      currLang: language.default,
      onChange: iframeManagerOnChange,
      services: {
        [airtableCookie.name]: {
          ...airtableCookie.iframe,
          languages: iframeLanguages(airtableCookie, "Pour vous inscrire"),
        },
        [youtubeCookie.name]: {
          ...youtubeCookie.iframe,
          languages: iframeLanguages(youtubeCookie, "Pour voir la vidéo"),
        },
      },
    });

    void (async (): Promise<void> => {
      await run({
        categories: {
          [CookieCategory.ANALYTICS]: {
            services: {
              [datadogCookie.name]: datadogCookie.service,
            },
          },
          [CookieCategory.FEATURES]: {
            services: {
              [airtableCookie.name]: {
                ...airtableCookie.service,
                ...iframeService(airtableCookie),
              },
              [youtubeCookie.name]: {
                ...youtubeCookie.service,
                ...iframeService(youtubeCookie),
              },
            },
          },
          [CookieCategory.NECESSARY]: {
            enabled: true,
            readOnly: true,
          },
        },
        language,
      });
    })();
  }, [iframeManager]);

  return (
    <button
      className="cursor-pointer"
      data-cc="show-preferencesModal"
      type="button"
    >
      {showPreferencesLabel}
    </button>
  );
}
